// The telemetry emitter (D212, KOLONY-Telemetry-Design.md §6).
//
// Fire-and-forget by contract: an emit can never throw, block, or slow the game, and a
// lost batch is a lost batch — there is no retry. When disabled (the Settings toggle off,
// or the browser sending Do Not Track) emits are no-ops and nothing is queued at all:
// "off" means the data never exists, not that it waits in memory.
//
// A guest's flushes ride `navigator.sendBeacon` — the one API built for "the page is
// leaving" — with a keepalive fetch fallback for environments without it (jsdom, old WebKit).
//
// D258: A SIGNED-IN SESSION'S BATCH CARRIES ITS BEARER, and so it cannot ride sendBeacon,
// which has no way to set a header. Until D258 no beacon ever carried one, so the server's
// "a verified bearer overrides the claimed identity" never fired: every event of every
// account was filed under its browser's guest id, and deleting the account removed none of
// them. A keepalive fetch survives the page leaving just as a beacon does, and CAN carry the
// header — and it never falls back to sendBeacon, because a batch stripped of its credential
// is a batch filed under the wrong identity, which is worse than a batch lost.
//
// The credential is captured when an event is EMITTED, never when it is flushed: an event
// belongs to whoever caused it. So a guest's events still queued when they sign in go out as
// a guest's, the sign-in event itself goes out under the account it created, and a flush
// that straddles the moment is one request per credential.

export type EventKind = 'hello' | 'funnel' | 'perf' | 'loop' | 'error' | 'net';

const FLUSH_AT = 10;
const FLUSH_EVERY_MS = 15_000;
const MAX_DATA_BYTES = 1024; // the server drops oversize events; do not ship what will be dropped

export interface TelemetryOpts {
  /** The stable identity the game already has (guest hex; a verified bearer upgrades it to the account). */
  identity: () => string;
  /**
   * D258: the session's bearer token, or null for a guest. Read per EMIT, like `enabled`,
   * and stamped on the event — so the credential an event is sent with is the one in force
   * when it happened, whatever has changed by the time it is flushed. REQUIRED, not
   * optional: the one caller is main.ts, which no coverage run measures, and an optional
   * credential is exactly how "the beacon never carried one" survived a green suite.
   */
  bearer: () => string | null;
  /** settings.shareUsage AND not DNT — evaluated per emit, so the toggle applies live. */
  enabled: () => boolean;
  endpoint?: string;
  /**
   * Injected in tests. `bearer` is the credential every event in `body` was emitted under; a
   * returned promise settles when the request has landed (or failed) — `flush()` reports it.
   */
  send?: (url: string, body: string, bearer: string | null) => Promise<void> | void;
  setTimer?: (fn: () => void, ms: number) => number;
  clearTimer?: (h: number) => void;
}

export interface Telemetry {
  emit(kind: EventKind, data?: Record<string, unknown>): void;
  /**
   * Push whatever is queued now (page hide, teardown, sign-out). Safe to call empty.
   *
   * The promise settles once EVERY batch this emitter still has in flight has landed or
   * failed — not only the ones this call sent, because a timed flush a moment earlier is just
   * as much in the air. It never rejects, and only sign-out waits for it (D258): a logout
   * issued behind a beacon still overtook it, measured on the first try, and the beacon was
   * then filed under the guest id because its token had been revoked in the same millisecond.
   */
  flush(): Promise<void>;
  /**
   * D258: drop everything queued, unsent. For account deletion: what is held on an account's
   * behalf must never reach the server after the account is gone — a dead bearer no longer
   * verifies, so it would be filed under the guest trail the teardown had just scrubbed.
   */
  discard(): void;
  dispose(): void;
}

/**
 * The default transport: a guest's batch as a beacon, a signed-in batch as a fetch carrying
 * its bearer. A beacon reports nothing back, so only a fetch has a landing to wait for.
 */
function defaultSend(url: string, body: string, bearer: string | null): Promise<void> | void {
  try {
    if (bearer === null) {
      const nav = globalThis.navigator as Navigator | undefined;
      if (nav?.sendBeacon?.(url, new Blob([body], { type: 'application/json' }))) return;
    }
    const headers: Record<string, string> = { 'content-type': 'application/json' };
    if (bearer !== null) headers['authorization'] = `Bearer ${bearer}`;
    return fetch(url, { method: 'POST', headers, body, keepalive: true }).then(
      () => undefined,
      () => undefined,
    );
  } catch {
    // a beacon is never worth an error
  }
}

interface Queued {
  kind: EventKind;
  data: Record<string, unknown>;
  identity: string;
  bearer: string | null;
}

export function createTelemetry(opts: TelemetryOpts): Telemetry {
  const endpoint = opts.endpoint ?? '/api/v1/beacon';
  const setTimer = opts.setTimer ?? ((fn, ms) => globalThis.setInterval(fn, ms) as unknown as number);
  const clearTimer = opts.clearTimer ?? ((h) => globalThis.clearInterval(h));
  const send = opts.send ?? defaultSend;

  let queue: Queued[] = [];
  /** Requests sent and not yet landed, each already made unable to reject. */
  const inFlight = new Set<Promise<void>>();
  const timer = setTimer(() => void flush(), FLUSH_EVERY_MS);

  function track(sent: Promise<void> | void): void {
    if (!sent) return;
    const landed = sent.then(
      () => undefined,
      () => undefined,
    );
    inFlight.add(landed);
    void landed.then(() => inFlight.delete(landed));
  }

  function flush(): Promise<void> {
    const batch = queue;
    queue = [];
    // One request per credential, in the order each first appeared. Almost every flush is a
    // single group; two only when the queue spans a sign-in or a sign-out.
    const groups = new Map<string, Queued[]>();
    for (const e of batch) {
      const key = JSON.stringify([e.identity, e.bearer]);
      const group = groups.get(key);
      if (group) group.push(e);
      else groups.set(key, [e]);
    }
    for (const group of groups.values()) {
      const head = group[0]!;
      try {
        track(send(endpoint, JSON.stringify({ identity: head.identity, events: group.map(({ kind, data }) => ({ kind, data })) }), head.bearer));
      } catch {
        // dropped, by design
      }
    }
    return Promise.all([...inFlight]).then(() => undefined);
  }

  return {
    emit(kind, data = {}): void {
      try {
        if (!opts.enabled()) return; // off ⇒ the data never exists
        if (JSON.stringify(data).length > MAX_DATA_BYTES) return; // the server would drop it anyway
        queue.push({ kind, data, identity: opts.identity(), bearer: opts.bearer() });
        if (queue.length >= FLUSH_AT) void flush();
      } catch {
        // an unserialisable payload is the caller's bug, never the player's crash
      }
    },
    flush,
    discard(): void {
      queue = [];
    },
    dispose(): void {
      void flush();
      clearTimer(timer);
    },
  };
}

/** The browser's own opt-out signal; checked alongside the Settings toggle. */
export function doNotTrack(): boolean {
  const nav = globalThis.navigator as (Navigator & { doNotTrack?: string }) | undefined;
  return nav?.doNotTrack === '1';
}

/**
 * One 5-second fps sample, taken ~10 s into a game — long enough for the loading gate,
 * the promotion and the opening scramble to be over, short enough to describe steady
 * state. Emits a single `perf` event; the returned cancel stops everything (flow
 * teardown). rAF-driven, so a hidden tab simply never finishes — and never lies.
 */
export function sampleFps(opts: {
  emit: Telemetry['emit'];
  gl?: () => string | null;
  tier?: string;
  delayMs?: number;
  windowMs?: number;
  raf?: (fn: () => void) => number;
  caf?: (h: number) => void;
  now?: () => number;
  setTimeout_?: (fn: () => void, ms: number) => number;
  clearTimeout_?: (h: number) => void;
}): () => void {
  const raf = opts.raf ?? ((fn) => globalThis.requestAnimationFrame(fn));
  const caf = opts.caf ?? ((h) => globalThis.cancelAnimationFrame(h));
  const now = opts.now ?? ((): number => performance.now());
  const setT = opts.setTimeout_ ?? ((fn, ms) => globalThis.setTimeout(fn, ms) as unknown as number);
  const clearT = opts.clearTimeout_ ?? ((h) => globalThis.clearTimeout(h));
  const windowMs = opts.windowMs ?? 5000;
  let rafHandle: number | null = null;
  let cancelled = false;

  const timer = setT(() => {
    const started = now();
    let frames = 0;
    const tick = (): void => {
      if (cancelled) return;
      frames += 1;
      const elapsed = now() - started;
      if (elapsed >= windowMs) {
        const fps = Math.round((frames / elapsed) * 1000);
        opts.emit('perf', { fps, ...(opts.gl ? { gl: opts.gl() ?? undefined } : {}), ...(opts.tier ? { tier: opts.tier } : {}) });
        return;
      }
      rafHandle = raf(tick);
    };
    rafHandle = raf(tick);
  }, opts.delayMs ?? 10_000);

  return () => {
    cancelled = true;
    clearT(timer);
    if (rafHandle !== null) caf(rafHandle);
  };
}
